/* eslint-disable */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { config } from '../config';
import { ApiError, NetworkError, ServerError, ValidationError } from './errors';
import { logError } from '../utils/errorHandling';

interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  retryableStatuses: number[];
}

/**
 * Base API Client - provides axios instance with interceptors
 */
export class ApiClient {
  private client: AxiosInstance;
  private retryConfig: RetryConfig = {
    maxRetries: 1,
    retryDelay: 1000, // 1 second
    retryableStatuses: [408, 429, 500, 502, 503, 504] // Retryable status codes
  };

  constructor(additionalHeaders?: Record<string, string>) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      // The API answers a request without it in the shapes of the release
      // before, for storefront code that is still open in a browser.
      'x-api-version': '2',
    };

    // Add rate limit whitelist key only on server-side (SSR)
    if (typeof window === 'undefined' && process.env.RATE_LIMIT_WHITELIST_KEY) {
      headers['rate-limit-whitelist-key'] = process.env.RATE_LIMIT_WHITELIST_KEY;
      // console.log(' ApiClient: Rate limit whitelist key added to headers');
    } else if (typeof window === 'undefined') {
      console.warn(' ApiClient: Running on server but RATE_LIMIT_WHITELIST_KEY not found');
    }

    // Merge additional headers (e.g., x-client-ip for SSR)
    if (additionalHeaders) {
      Object.assign(headers, additionalHeaders);
    }

    this.client = axios.create({
      baseURL: config.GATEWAY_API_URL,
      headers,
      withCredentials: true,
      fetchOptions: { next: { revalidate: 60 }, },
      timeout: 10000, // 10 seconds timeout
    });

    this.client.interceptors.request.use((reqConfig) => {
      if (typeof window !== 'undefined') {
        const match = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]*)/);
        if (match?.[1]) {
          reqConfig.headers.set('x-user-locale', match[1]);
        }
      }
      return reqConfig;
    });

    // Add response interceptor
    this.client.interceptors.response.use(
      (response) => {
        // Calculate request duration for logging/monitoring
        const config = response.config as any;
        if (config.metadata) {
          const endTime = new Date().getTime();
          const duration = endTime - config.metadata.startTime;
          
          // Log slow requests for performance monitoring
          if (duration > 1000) { // Over 1 second
            console.warn(`Slow API request (${duration}ms): ${config.method?.toUpperCase()} ${config.url}`);
          }
        }
        
        return response;
      },
      async (error: AxiosError) => {
        // Implement retry logic for network errors and certain status codes
        const config = error.config as any;
        
        // Initialize retry count if not present
        if (!config.metadata) {
          config.metadata = { startTime: new Date().getTime() };
        }
        
        const retryCount = config.metadata.retryCount || 0;

        // retry is for pussies
        const skipRetry = true;

        // Check if we should retry
        const shouldRetry =
          !skipRetry &&
          retryCount < this.retryConfig.maxRetries &&
          (
            !error.response || // Network errors
            (error.response && this.retryConfig.retryableStatuses.includes(error.response.status))
          );
        
        if (shouldRetry) {
          config.metadata.retryCount = retryCount + 1;
          
          // Wait before retrying
          await new Promise(resolve => 
            setTimeout(resolve, this.retryConfig.retryDelay * config.metadata.retryCount)
          );
          
          // Retry the request
          return this.client(config);
        }
        
        // Transform axios errors to our custom error types
        if (error.response) {
          const { status, data } = error.response;
          
          // Extract error code + details from response data. The BE wire shape
          // is `{ statusCode, errorCode, details? }` - no message field. The FE
          // hook (useErrorMessage) renders user-facing text by looking up the
          // code in errors.json. We fall back to axios's default message only
          // for non-API errors (network, 5xx with no body, legacy responses).
          let errorCode: string | undefined;
          let details: Record<string, unknown> | undefined;
          if (data && typeof data === 'object') {
            if ('errorCode' in data && typeof data.errorCode === 'string') {
              errorCode = data.errorCode;
            }
            if (
              'details' in data &&
              data.details &&
              typeof data.details === 'object'
            ) {
              details = data.details as Record<string, unknown>;
            }
          }
          // Surface the code in Error.message so dev tools / logs are readable
          // (instead of "Request failed with status code 401").
          const message = errorCode ?? error.message;
          
          logError('ApiClient', error, {
            status,
            url: config.url,
            method: config.method
          });

          // The user's session is technically valid (refresh-token cookie
          // hasn't expired) but the account was disabled/deleted while they
          // were logged in. Let AuthContext clear local state and bounce.
          if (errorCode === 'USER_INACTIVE' && typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('aceb:auth-revoked', { detail: { message } }),
            );
          }

          if (status === 422) {
            throw new ValidationError(message);
          } else if (status >= 500) {
            throw new ServerError(message);
          } else {
            throw new ApiError(status, message, errorCode, details);
          }
        } else if (error.request) {
          // Request made but no response received
          const networkError = new NetworkError('Network error: no response received');
          logError('ApiClient', networkError, { url: config.url, method: config.method });
          throw networkError;
        } else {
          // Error in setting up the request
          logError('ApiClient', error, { url: config.url, method: config.method });
          throw error;
        }
      }
    );
  }

  public get<T>(url: string, params = {}): Promise<T> {
    return this.client.get<T>(url, { params }).then(response => response.data);
  }

  public post<T>(url: string, data = {}, config = {}): Promise<T> {
    return this.client.post<T>(url, data, config).then(response => response.data);
  }

  public put<T>(url: string, data = {}): Promise<T> {
    return this.client.put<T>(url, data).then(response => response.data);
  }

  public patch<T>(url: string, data = {}): Promise<T> {
    return this.client.patch<T>(url, data).then(response => response.data);
  }

  public delete<T>(url: string): Promise<T> {
    return this.client.delete<T>(url).then(response => response.data);
  }

  public deleteWithBody<T>(url: string, data = {}): Promise<T> {
    return this.client.delete<T>(url, { data }).then(response => response.data);
  }

  public postFormData<T>(url: string, formData: FormData, config = {}): Promise<T> {
    const finalConfig = {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...((config as any).headers || {})
      }
    };
    return this.client.post<T>(url, formData, finalConfig).then(response => response.data);
  }
}